/** Tiled Islamic geometric background pattern */
export function IslamicGeometricBg() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.025]"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="contact-geo-tile"
          x="0"
          y="0"
          width="80"
          height="80"
          patternUnits="userSpaceOnUse"
        >
          <g fill="none" stroke="#ffb606" strokeWidth="0.6">
            <polygon points="40,5 55,20 75,20 75,40 75,60 55,60 40,75 25,60 5,60 5,40 5,20 25,20" />
            <circle cx="40" cy="40" r="10" />
            <line x1="40" y1="5" x2="40" y2="75" />
            <line x1="5" y1="40" x2="75" y2="40" />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#contact-geo-tile)" />
    </svg>
  )
}

/** Subtle radial glow orbs for background depth */
export function FloatingOrbs() {
  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full opacity-[0.06]"
        style={{
          background: 'radial-gradient(circle, #ffb606 0%, transparent 70%)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full opacity-[0.05]"
        style={{
          background: 'radial-gradient(circle, #004d40 0%, transparent 70%)',
        }}
      />
    </>
  )
}

/** Islamic compass / world-map decoration used in the info panel */
export function IslamicCompass() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/8 bg-white/5 p-6">
      <svg
        viewBox="0 0 200 200"
        className="mx-auto w-full max-w-[180px] opacity-60"
        aria-hidden="true"
      >
        {/* Rings */}
        <circle
          cx="100"
          cy="100"
          r="90"
          fill="none"
          stroke="#ffb606"
          strokeWidth="0.8"
          opacity="0.3"
        />
        <circle
          cx="100"
          cy="100"
          r="70"
          fill="none"
          stroke="#ffb606"
          strokeWidth="0.5"
          opacity="0.2"
        />
        <circle
          cx="100"
          cy="100"
          r="50"
          fill="none"
          stroke="#ffb606"
          strokeWidth="0.4"
          opacity="0.15"
        />

        {/* 8 compass arms */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
          const rad = (deg * Math.PI) / 180
          return (
            <line
              key={i}
              x1={100 + 52 * Math.cos(rad)}
              y1={100 + 52 * Math.sin(rad)}
              x2={100 + 88 * Math.cos(rad)}
              y2={100 + 88 * Math.sin(rad)}
              stroke="#ffb606"
              strokeWidth="0.7"
              opacity="0.4"
            />
          )
        })}

        {/* Center jewel */}
        <circle cx="100" cy="100" r="6" fill="#ffb606" opacity="0.6" />
        <circle cx="100" cy="100" r="3" fill="#fff" opacity="0.9" />

        {/* 8-pointed star overlay */}
        <polygon
          points="100,55 103,95 140,100 103,105 100,145 97,105 60,100 97,95"
          fill="none"
          stroke="#ffb606"
          strokeWidth="0.8"
          opacity="0.35"
        />

        {/* Cardinal labels */}
        {[
          { label: 'N', x: 100, y: 14 },
          { label: 'S', x: 100, y: 193 },
          { label: 'E', x: 186, y: 103 },
          { label: 'W', x: 14, y: 103 },
        ].map(({ label, x, y }) => (
          <text
            key={label}
            x={x}
            y={y}
            textAnchor="middle"
            fill="#ffb606"
            fontSize="10"
            opacity="0.6"
            fontFamily="sans-serif"
          >
            {label}
          </text>
        ))}
      </svg>

      <p className="mt-4 text-center text-sm text-neutral-400">
        Serving students across the globe
      </p>
      <div className="mt-2 flex items-center justify-center gap-2">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-400" />
        <span className="text-xs font-semibold text-primary-400">
          Online & Available 24/7
        </span>
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-400" />
      </div>
    </div>
  )
}

/** Wavy Arabic-style border divider */
export function WavyDivider({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 800 40"
      preserveAspectRatio="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M0,20 Q25,5 50,20 Q75,35 100,20 Q125,5 150,20 Q175,35 200,20 Q225,5 250,20 Q275,35 300,20 Q325,5 350,20 Q375,35 400,20 Q425,5 450,20 Q475,35 500,20 Q525,5 550,20 Q575,35 600,20 Q625,5 650,20 Q675,35 700,20 Q725,5 750,20 Q775,35 800,20"
        fill="none"
        stroke="#ffb606"
        strokeWidth="1.2"
      />
    </svg>
  )
}

/** Star-tile pattern rect — reusable with a unique pattern ID */
export function StarTileRect({
  patternId,
  className,
}: {
  patternId: string
  className?: string
}) {
  return (
    <svg
      className={
        className ?? 'pointer-events-none absolute inset-0 h-full w-full'
      }
      aria-hidden="true"
    >
      <defs>
        <pattern
          id={patternId}
          x="0"
          y="0"
          width="60"
          height="60"
          patternUnits="userSpaceOnUse"
        >
          <polygon
            points="30,3 33,24 50,15 40,30 56,38 35,38 30,55 25,38 4,38 20,30 10,15 27,24"
            fill="none"
            stroke="#ffb606"
            strokeWidth="0.6"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  )
}
